import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const ACCENTS = {
  blue: { hex: '#0052E6', hover: '#0041B8' },
  orange: { hex: '#E8510A', hover: '#C74408' },
  green: { hex: '#2C6B42', hover: '#225434' },
};

const AccentContext = createContext({
  active: { index: 0, total: 0, accent: 'blue', id: null },
  register: () => () => {},
});

export function AccentProvider({ children }) {
  const [active, setActive] = useState({ index: 0, total: 0, accent: 'blue', id: null });
  const metaRef = useRef(new Map());
  const observerRef = useRef(null);
  const visibleRef = useRef(new Set());

  const recompute = useCallback(() => {
    const metas = [...metaRef.current.values()];
    const total = metas.filter((m) => m.counted !== false).length;
    let current = null;
    for (const el of visibleRef.current) {
      const meta = metaRef.current.get(el);
      if (meta && (!current || meta.index > current.index)) current = meta;
    }
    setActive((prev) => {
      // 中央帯にセクションがない間（フッター等）は直前のセクションを保持する。
      // セクションが全て消えたとき（別ページ）だけブルーへ戻す。
      const hold = !current && total > 0;
      const next = {
        index: current ? current.index : hold ? prev.index : 0,
        total,
        accent: current ? current.accent : hold ? prev.accent : 'blue',
        id: current ? current.id : hold ? prev.id : null,
      };
      if (
        prev.index === next.index &&
        prev.total === next.total &&
        prev.accent === next.accent &&
        prev.id === next.id
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    // 画面中央の帯に重なっているセクションをアクティブとみなす
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visibleRef.current.add(entry.target);
          else visibleRef.current.delete(entry.target);
        }
        recompute();
      },
      { rootMargin: '-40% 0px -52% 0px', threshold: 0 }
    );
    // 子SectionのuseEffectは親より先に走るため、observer生成前にregister済みの
    // 要素がここに残っている。まとめてobserveしないと初回ロードのページで
    // --accent がデフォルトの青のまま固まる（本番ビルドのみ再現）。
    for (const el of metaRef.current.keys()) observerRef.current.observe(el);
    return () => {
      observerRef.current?.disconnect();
      visibleRef.current.clear();
    };
  }, [recompute]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--accent',
      ACCENTS[active.accent]?.hex ?? ACCENTS.blue.hex
    );
  }, [active.accent]);

  const register = useCallback(
    (el, meta) => {
      if (!el) return () => {};
      metaRef.current.set(el, meta);
      observerRef.current?.observe(el);
      recompute();
      return () => {
        metaRef.current.delete(el);
        visibleRef.current.delete(el);
        observerRef.current?.unobserve(el);
        recompute();
      };
    },
    [recompute]
  );

  const value = useMemo(() => ({ active, register }), [active, register]);
  return <AccentContext.Provider value={value}>{children}</AccentContext.Provider>;
}

export function useAccent() {
  return useContext(AccentContext);
}

/**
 * ホームの各セクションを包むラッパー。
 * AccentProviderに自身を登録し、画面中央に来ると --accent が切り替わる。
 */
export function Section({
  id,
  index,
  accent = 'blue',
  counted = true,
  className = '',
  children,
  ...rest
}) {
  const ref = useRef(null);
  const { register } = useAccent();

  useEffect(
    () => register(ref.current, { id, index, accent, counted }),
    [register, id, index, accent, counted]
  );

  return (
    <section ref={ref} id={id} data-accent={accent} className={className} {...rest}>
      {children}
    </section>
  );
}
